import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, CircularProgress } from '@mui/material';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation/Navigation';
import Login from './components/Auth/Login';
import TeamSelector from './components/Auth/TeamSelector';
import Dashboard from './components/Dashboard/Dashboard';
import TeamMembers from './components/TeamMembers/TeamMembers';
import Goals from './components/Goals/Goals';
import Tasks from './components/Tasks/Tasks';
import Workshops from './components/Workshops/Workshops';
import GoalMapping from './components/Visualizations/GoalMapping';
import EisenhowerMatrix from './components/Visualizations/EisenhowerMatrix';
import SkillsMentoring from './components/Visualizations/SkillsMentoring';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  typography: {
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 500,
    },
  },
});

const AppContent: React.FC = () => {
  const { user, currentTeam, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // User not authenticated - show login for both / and /verify routes
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/verify" element={<Login />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // User authenticated but no team selected
  if (!currentTeam) {
    return <TeamSelector />;
  }

  // User authenticated and team selected - show main app
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Navigation />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/team" element={<TeamMembers />} />
          <Route path="/goals" element={<Goals />} />
          <Route path="/tasks" element={<Tasks view="overview" />} />
          <Route path="/workshops" element={<Workshops />} />
          <Route path="/goal-mapping" element={<GoalMapping />} />
          <Route path="/eisenhower-matrix" element={<EisenhowerMatrix />} />
          <Route path="/skills-mentoring" element={<SkillsMentoring />} />
        </Routes>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
